import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import teamService from '../../services/teamService';

export const HeaderTeamDropdown = ({
  label = 'TEAM',
  selectedTeam = null,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
    }
  }, []);

  const fetchTeams = useCallback(async (search = '', pg = 1, append = false) => {
    if (pg === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const data = await teamService.getPlayerTeams({ page: pg, limit: 20, teamName: search });
      const list = data.teams || data.data || [];
      const total = data.pagination?.totalPages || data.totalPages || 1;
      setTeams((prev) => append ? [...prev, ...list] : list);
      setPage(pg);
      setHasMore(pg < total);
    } catch (_) {
      // silent
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Fetch teams when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchTeams('', 1, false);
      updatePosition();
    }
  }, [isOpen, fetchTeams, updatePosition]);

  // Close on outside click + scroll tracking
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => updatePosition();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updatePosition]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchTeams(value, 1, false);
    }, 500);
  };

  const handleListScroll = () => {
    if (!listRef.current || loading || loadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 30) {
      fetchTeams(searchTerm, page + 1, true);
    }
  };

  const handleSelectTeam = (team) => {
    onSelect(team);
    setIsOpen(false);
    setSearchTerm('');
  };

  const hasValue = selectedTeam !== null;
  const displayName = selectedTeam?.name || selectedTeam?.teamName || null;

  const dropdown = isOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
          className="bg-jp-section border border-jp-line rounded-lg shadow-xl min-w-[220px] overflow-hidden"
        >
          {/* Search */}
          <div className="p-2 border-b border-jp-line">
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              autoFocus
              className="w-full px-2 py-1.5 text-[11px] rounded bg-jp-section border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          {/* List */}
          <div ref={listRef} onScroll={handleListScroll} className="max-h-48 overflow-y-auto">
            {/* All / Clear option */}
            <button
              onClick={() => handleSelectTeam(null)}
              className={`w-full text-left px-3 py-1.5 text-[11px] transition-colors border-b border-jp-line ${
                !selectedTeam ? 'bg-jp-accent/15 text-jp-accent-ink font-semibold' : 'text-jp-muted hover:bg-jp-line'
              }`}
            >
              All Teams
            </button>

            {loading && teams.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : teams.length > 0 ? (
              <>
                {teams.map((team) => {
                  const teamId = team._id || team.id;
                  const teamName = team.name || team.teamName;
                  const isSelected = selectedTeam?._id === teamId || selectedTeam?.id === teamId;
                  return (
                    <button
                      key={teamId}
                      onClick={() => handleSelectTeam(team)}
                      className={`w-full text-left px-3 py-1.5 text-[11px] transition-colors ${
                        isSelected ? 'bg-jp-accent/15 text-jp-accent-ink font-semibold' : 'text-jp-ink hover:bg-jp-line'
                      }`}
                    >
                      {teamName}
                    </button>
                  );
                })}
                {loadingMore && (
                  <div className="px-3 py-2 text-center">
                    <div className="w-3 h-3 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                )}
              </>
            ) : (
              <div className="px-3 py-4 text-center text-jp-muted text-[11px]">No teams found</div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center gap-1 cursor-pointer hover:text-jp-accent-ink transition-colors w-full"
      >
        <span>{label}</span>
        {hasValue && (
          <span className="bg-cyan-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            ✓
          </span>
        )}
        <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </button>
      {dropdown}
    </>
  );
};

export default HeaderTeamDropdown;
